import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MarketingLayout, Section, Overline } from "@/marketing/MarketingLayout";
import { ABOUT, PLAN } from "@/marketing/content";
import { ArrowRight, Target, ShieldCheck, HeartHandshake, Wallet } from "lucide-react";

const VALUE_ICONS = [Target, ShieldCheck, Wallet, HeartHandshake];

export default function MarketingAbout() {
  const navigate = useNavigate();
  return (
    <MarketingLayout>
      <Section className="pt-16 pb-4">
        <Overline>{ABOUT.hero.eyebrow}</Overline>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-5 max-w-3xl">{ABOUT.hero.headline}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl">{ABOUT.hero.subheadline}</p>
      </Section>

      <Section className="pt-10">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {ABOUT.stats.map((s) => (
            <div key={s.label} className="p-5 rounded-md border border-border bg-card text-center" data-testid="about-stat">
              <div className="font-mono text-2xl font-black text-primary">{s.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="pt-16 grid lg:grid-cols-2 gap-10">
        <div>
          <Overline>Our story</Overline>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-5">{ABOUT.story.heading}</h2>
          {ABOUT.story.paragraphs.map((p, i) => (
            <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-4">{p}</p>
          ))}
        </div>
        <div className="rounded-md border border-primary/40 bg-card p-8 self-start">
          <Overline>Our mission</Overline>
          <h3 className="font-heading text-xl font-bold tracking-tight mb-3">{ABOUT.mission.heading}</h3>
          <p className="text-muted-foreground leading-relaxed italic">"{ABOUT.mission.statement}"</p>
        </div>
      </Section>

      <Section className="pt-16">
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-6">What we stand for</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ABOUT.mission.values.map((v, i) => {
            const Icon = VALUE_ICONS[i];
            return (
              <div key={v.title} className="p-6 rounded-md border border-border bg-card" data-testid="about-value">
                <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-primary" /></div>
                <h3 className="font-heading font-bold text-base mb-2">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.description}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section className="pt-16">
        <Overline>Leadership</Overline>
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">{ABOUT.team.heading}</h2>
        <p className="text-muted-foreground mb-8 max-w-2xl">{ABOUT.team.subheading}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {ABOUT.team.members.map((m) => (
            <div key={m.name} className="p-6 rounded-md border border-border bg-card flex gap-4" data-testid="team-member">
              <div className="w-14 h-14 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <span className="font-heading font-black text-lg text-primary">{m.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</span>
              </div>
              <div>
                <h3 className="font-heading font-bold">{m.name}</h3>
                <p className="text-xs font-mono uppercase tracking-wider text-primary mb-2">{m.role}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{m.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="pt-16">
        <div className="rounded-md bg-primary text-primary-foreground p-10 text-center">
          <h2 className="font-heading text-3xl font-black tracking-tight mb-3">Ready to Work With Us?</h2>
          <p className="opacity-90 max-w-2xl mx-auto mb-7">Join 1,000+ freshers who trusted EliteHire India to launch their careers.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button size="lg" variant="secondary" onClick={() => navigate("/register")} className="rounded-md gap-2 h-12 px-6" data-testid="about-register-cta">
              {PLAN.cta} <ArrowRight className="w-4 h-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/contact")} className="rounded-md h-12 px-6 bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10" data-testid="about-contact-cta">
              Contact Us
            </Button>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
